// Próximos vencimentos de todas as abas, num lugar só (painel do My Planner):
// documentos, garantias, contas a pagar e as datas com aviso das abas de fichas (vacinas, IPVA, devoluções...).
import type { AbaId, Docs } from '../store/types';
import { MOD_IDS } from '../store/types';
import { DEFS } from '../modulos/defs';
import { ctxDe } from '../modulos/ctx';
import { warrantyEnd } from '../store/warranty';
import { faturasNoPeriodo, ocorrencias } from '../store/financas';
import { addDays, today } from './date';
import { t } from './i18n';
import { parteLigada } from './partes';
import { LISTA_CONTAS, LISTA_ITENS, listaComData, listaNoPlanner } from './enviarPlanner';

export type Vencimento = { data: string; titulo: string; detalhe: string; aba: AbaId; id: string; icone: string };

/** Tudo o que vence de "atrasado há até 7 dias" até daqui a N dias, em ordem de data. */
export function proximosVencimentos(docs: Docs, liberada: (id: AbaId) => boolean, dias = 30, faixa?: { de: string; ate: string; semFinancas?: boolean }): Vencimento[] {
  const de = faixa?.de ?? addDays(today(), -7), ate = faixa?.ate ?? addDays(today(), dias);
  if (faixa?.semFinancas) { const lib0 = liberada; liberada = id => id !== 'financas' && lib0(id); }
  const dentro = (d?: string | null) => !!d && d >= de && d <= ate;
  // C1/C2: nada das outras abas entra sozinho, só as listas enviadas ao My Planner; D1: parte desligada não entra
  const aj = docs.ajustes;
  const contasNoPlanner = listaNoPlanner(aj, 'financas', LISTA_CONTAS);
  const out: Vencimento[] = [];
  if (liberada('documentos') && listaNoPlanner(aj, 'documentos', LISTA_ITENS)) for (const d of docs.documentos?.items ?? [])
    if (dentro(d.validade)) out.push({ data: d.validade!, titulo: d.nome?.trim() || t(d.tipo), detalhe: t('Validade'), aba: 'documentos', id: d.id, icone: '🪪' });
  if (liberada('garantias') && listaNoPlanner(aj, 'garantias', LISTA_ITENS)) for (const w of docs.garantias?.items ?? []) {
    const fim = warrantyEnd(w);
    if (dentro(fim)) out.push({ data: fim, titulo: w.name, detalhe: t('Fim da garantia'), aba: 'garantias', id: w.id, icone: '🧾' });
  }
  if (liberada('financas') && contasNoPlanner) for (const c of docs.financas?.contas ?? []) {
    if (c.tipo !== 'pagar') continue;
    for (const v of ocorrencias(c, de, ate)) if (!c.pagos.includes(v)) out.push({ data: v, titulo: c.nome, detalhe: t('Conta a pagar'), aba: 'financas', id: c.id, icone: c.icone || '💰' });
  }
  if (liberada('financas') && contasNoPlanner && docs.financas) for (const f of faturasNoPeriodo(docs.financas, de, ate))
    if (!f.paga) out.push({ data: f.venc, titulo: t('Fatura {c}', { c: f.cartao.nome }), detalhe: t('Conta a pagar'), aba: 'financas', id: f.cartao.id, icone: '💳' });
  for (const mod of MOD_IDS) {
    if (!liberada(mod)) continue;
    const regs = docs[mod]?.regs ?? [];
    if (!regs.length) continue;
    const def = DEFS[mod], ctx = ctxDe(mod, regs);
    for (const r of regs) {
      const col = def.colecoes.find(c => c.id === r.col);
      if (!col || col.feito?.(r)) continue;
      // texto 28 (C3): lista enviada "só os valores a receber" (ex.: cobranças dos pacientes do Consultório)
      const ar = col.aReceberNoPlanner;
      if (ar && listaNoPlanner(aj, mod, col.id) && parteLigada(aj?.partesOcultas, mod, col.id) && ar.incluir(r) && dentro(r.c[ar.data])) {
        const pai = r.pai ? ctx.porId(r.pai) : null;
        const cp = pai ? def.colecoes.find(c => c.id === pai.col) : null;
        out.push({ data: r.c[ar.data], titulo: col.titulo(r, ctx) || t(col.um), detalhe: [t(ar.rotulo), pai && cp ? cp.titulo(pai, ctx) : ''].filter(Boolean).join(' · '),
          aba: mod, id: r.id, icone: '💰' });
        continue;
      }
      if (!parteLigada(aj?.partesOcultas, mod, col.id) || (listaComData(col) && !listaNoPlanner(aj, mod, col.id))) continue;
      for (const f of col.campos) {
        if (f.t !== 'data' || !f.aviso?.length || !dentro(r.c[f.k])) continue;
        // data que já passou só conta como atraso quando dá para marcar como feito (pago, devolvido...)
        // e quando a data é um prazo (aviso no dia ou depois); check-in que já passou, por exemplo, não é atraso
        if (r.c[f.k] < today() && (!col.feito || !f.aviso.some(n => n <= 0))) continue;
        const pai = r.pai ? ctx.porId(r.pai) : null;
        const cp = pai ? def.colecoes.find(c => c.id === pai.col) : null;
        out.push({ data: r.c[f.k], titulo: col.titulo(r, ctx) || t(col.um), detalhe: [t(f.r), pai && cp ? cp.titulo(pai, ctx) : ''].filter(Boolean).join(' · '),
          aba: mod, id: r.id, icone: typeof col.icone === 'function' ? col.icone(r) : col.icone });
      }
    }
  }
  // texto 10 (E3): item "Aguardando confirmação" não entra pela lista enviada (só se a pessoa enviar o item de propósito)
  const aguarda = docs.envios?.aguardando ?? {};
  return out.filter(v => !aguarda[`${v.aba}:${v.id}`]).sort((a, b) => a.data.localeCompare(b.data));
}
